import { useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrderWrapper } from 'Modules/order/components/OrderWrapper/OrderWrapper'
import {
  AccessoriesStep,
  AddressStep,
  DeviceStep,
  SummaryStep,
} from 'Modules/order/components/Steps'
import {
  useOfferOrderMutation,
  useRedirectToConfirmationPage,
} from 'Modules/order/hooks'
import { OrderFlowTypesEnum } from 'Modules/order/types/orderFlow.types'
import { showErrorModal } from 'Modules/order/utils/errorModal'
import { notEmpty } from 'Modules/order/utils/notEmpty.utils'

import { Stepper, StepperStep } from 'Components/Stepper'

import { useStore } from 'Stores/index'
import { UserModeEnum } from 'Stores/portalSettingsStore/portalSettingsStore.types'

import { useActiveUser, useIsOrganisation, useModal } from 'Hooks'

import {
  mutationNewDeviceCaseItem,
  mutationNewDeviceCaseItemVariables,
} from 'Constants/graphql/mutations/__generated__/mutationNewDeviceCaseItem'
import { MUTATION_NEW_DEVICE_CASEITEM } from 'Constants/graphql/mutations/MutationNewDeviceCaseItem'

import { OrderAddressType, OrderType } from 'Portal/__generated__/globalTypes'
import { productOffersDeviceItemPayloadMapper } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.mapper'
import { OrderDeviceResponseVariant } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'

export const OrderNew = observer(() => {
  const { orderStore, userStore, portalSettingsStore, navigationStore } =
    useStore()

  const {
    setMutationError,
    setOrderedDevices,
    orderDevices,
    chosenAccessories,
    remarks,
    chosenShippingAddress,
    chosenShippingAddressLabel,
    chosenDevice,
    shouldSkipAccessories,
  } = orderStore

  const { user } = userStore
  const { userMode } = portalSettingsStore
  const { mainNavigationConfig } = navigationStore
  const history = useHistory()

  const { setModal, hideModal } = useModal()

  const isOrganisation = useIsOrganisation()
  const { activeEmployee, activeOrganisation } = useActiveUser()
  const { employeeId = '' } = useParams<{ employeeId?: string }>()
  const activeEmployeeId = employeeId || activeEmployee?.id.toString()

  const shippingAddressOrderOfferPayload = {
    city: chosenShippingAddress?.city || '',
    country: chosenShippingAddress?.country || '',
    name: chosenShippingAddress?.name || '',
    nameAddition: chosenShippingAddress?.nameAddition || '',
    street: chosenShippingAddress?.street || '',
    streetAddition: chosenShippingAddress?.streetAddition || '',
    type: OrderAddressType.shipping,
    zipcode: chosenShippingAddress?.zipcode || '',
  }

  const { redirectToConfirmationPage } =
    useRedirectToConfirmationPage(activeEmployeeId)

  const { handleOfferOrderMutation, isOfferOrderLoading } =
    useOfferOrderMutation({
      handleOnCompletedOrder: () => {
        redirectToConfirmationPage()
      },
      variables: {
        input: {
          orders: [
            {
              employeeId: activeEmployeeId,
              items: productOffersDeviceItemPayloadMapper(
                chosenDevice,
                chosenAccessories.size
                  ? [...chosenAccessories].map(([_id, accessory]) => accessory)
                  : null,
              ),
              offerId: chosenDevice?.offerId || '',
              orderType: OrderType.newDevice,
              organisationId:
                activeEmployee?.organisation?.id.toString() ||
                activeOrganisation?.id.toString() ||
                '',
              remark: remarks,
              shippingAddress: shippingAddressOrderOfferPayload,
            },
          ],
          requestingUserID: user.userId?.toString() ?? '0',
        },
      },
    })

  const [createNewDeviceCaseItem, { loading: isMutationLoading }] = useMutation<
    mutationNewDeviceCaseItem,
    mutationNewDeviceCaseItemVariables
  >(MUTATION_NEW_DEVICE_CASEITEM, {
    onCompleted: data => {
      const metadata = {
        orderedDevice: chosenDevice?.deviceDefinition.name,
        quantity: chosenDevice?.quantity,
        userType: isOrganisation ? 'Admin' : 'Employee',
      }

      Intercom('trackEvent', 'new-order', metadata)

      if (data.createNewDeviceCaseItem.subCaseItems) {
        const filteredDevices =
          data.createNewDeviceCaseItem.subCaseItems.filter(notEmpty)

        setOrderedDevices(filteredDevices)
      } else {
        setOrderedDevices(Number(data.createNewDeviceCaseItem.id))
      }

      setMutationError(false)

      redirectToConfirmationPage()
    },
    onError: () => {
      setMutationError(true)

      showErrorModal(() => {
        hideModal()

        history.push(mainNavigationConfig.url)
      }, setModal)
    },
    variables: {
      input: {
        accessories: chosenAccessories.size
          ? [...chosenAccessories].map(([, accessory]) => ({
              customerLabel: accessory?.customerLabel ?? '',
              id: accessory.deviceDefinition.id,
              quantity: accessory.quantity,
            }))
          : null,
        comment: remarks,
        device: {
          customerLabel: chosenDevice?.customerLabel ?? '',
          id: chosenDevice?.deviceDefinition.id ?? '',
          quantity: chosenDevice?.quantity ?? 1,
        },
        employeeID: activeEmployeeId,
        organisationID:
          activeEmployee?.organisation?.id.toString() ||
          activeOrganisation?.id.toString() ||
          '',
        rentCategoryID: chosenDevice?.rentCategoryId || '',
        requestingUserID: user.userId?.toString() || '',
        shippingAddress: {
          _label:
            userMode !== UserModeEnum.NEW_JOINER
              ? chosenShippingAddressLabel
              : 'New joiner',
          city: chosenShippingAddress?.city || '',
          country: chosenShippingAddress?.country || '',
          name: chosenShippingAddress?.name || '',
          nameAddition: chosenShippingAddress?.nameAddition || '',
          street: chosenShippingAddress?.street || '',
          streetAddition: chosenShippingAddress?.streetAddition || '',
          zipcode: chosenShippingAddress?.zipcode || '',
        },
      },
    },
  })

  const orderNewStepsConfig = useMemo(() => {
    const baseSteps: StepperStep[] = [
      {
        headingLabel: 'NewOrder_choose_device_header',
        renderStepContent: ({ handleNextStep }) => (
          <DeviceStep handleNextStep={handleNextStep} />
        ),
        subHeadingLabel: 'NewOrder_choose_device_subheader',
      },
      {
        headingLabel: 'NewOrder_shipping_address_header',
        renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
          <AddressStep
            activeEmployeeId={activeEmployeeId}
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
          />
        ),
        subHeadingLabel: 'NewOrder_shipping_address_subheader',
      },
      {
        headingLabel: 'NewOrder_order_summary_header',
        renderStepContent: ({ handleSetCurrentStep, handlePreviousStep }) => (
          <SummaryStep
            activeEmployeeId={employeeId}
            handleOrderSubmit={() => {
              if (
                orderDevices?.variant === OrderDeviceResponseVariant.CATALOG
              ) {
                handleOfferOrderMutation()
              } else {
                createNewDeviceCaseItem()
              }
            }}
            handlePreviousStep={handlePreviousStep}
            isNewOrder
            isSubmitLoading={isMutationLoading || isOfferOrderLoading}
            setCurrentOrderStep={handleSetCurrentStep}
          />
        ),
        subHeadingLabel: 'NewOrder_order_summary_subheader',
      },
    ]

    const accessoriesStep: StepperStep = {
      headingLabel: 'NewOrder_choose_accessories_header',
      renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
        <AccessoriesStep
          handleNextStep={handleNextStep}
          handlePreviousStep={handlePreviousStep}
        />
      ),
      subHeadingLabel: 'NewOrder_choose_accessories_subheader',
    }

    if (shouldSkipAccessories) {
      return baseSteps
    }

    baseSteps.splice(1, 0, accessoriesStep)

    return baseSteps
  }, [
    activeEmployeeId,
    createNewDeviceCaseItem,
    employeeId,
    handleOfferOrderMutation,
    isMutationLoading,
    isOfferOrderLoading,
    orderDevices,
    shouldSkipAccessories,
  ])

  return (
    <OrderWrapper
      employeeId={activeEmployeeId}
      orderFlowType={OrderFlowTypesEnum.NEW_ORDER}
      renderOrderContent={() => <Stepper steps={orderNewStepsConfig} />}
    />
  )
})
